<script setup lang="ts">
/**
 * Module → topic → lesson picker. Tagging is optional. A topic absent from the latest catalogue is
 * rendered from the row's denormalized labels with a re-tag hint, never as a bare empty select.
 */

import { AppText } from '@shared/ui/typography'
import { AppSelect } from '@shared/ui/selects'
import { BtnSecondary } from '@shared/ui/buttons'
import { AppBadge } from '@shared/ui/badges'

import { useAssignmentTaxonomyPicker } from '../../../model/assignments/assignment-taxonomy-picker.hook'

defineOptions({
  name: 'AssignmentTaxonomyPicker',
})

const {
  refIsLoading,
  refCanPick,
  refModuleOptions,
  refTopicOptions,
  refLessonOptions,
  refModuleId,
  refTopicId,
  refLessonId,
  refIsCatalogueEmpty,
  refNeedsRetagging,
  refSavedLabel,
  clearTopic,
} = useAssignmentTaxonomyPicker()
</script>

<template>
  <div class="assignment-taxonomy-picker">
    <div class="assignment-taxonomy-picker__head">
      <AppText name="body1-medium">
        Curriculum
      </AppText>
      <AppText
        name="body2"
        color="slate-400"
      >
        Optional
      </AppText>
    </div>

    <!-- THE STALE STATE. Rendered ABOVE the selects, because it explains why they do not show the
         tagging the rest of the app displays for this template. -->
    <div
      v-if="refNeedsRetagging"
      class="assignment-taxonomy-picker__stale"
    >
      <div class="assignment-taxonomy-picker__stale-head">
        <AppText name="body2-medium">
          {{ refSavedLabel ?? 'This assignment has a topic that is no longer readable' }}
        </AppText>
        <AppBadge
          color="orange"
          text="Needs re-tagging"
        />
      </div>
      <AppText
        name="body2"
        color="slate-500"
      >
        This topic is no longer in the curriculum — the curriculum has moved on since the assignment
        was tagged. Pick a new topic below, or clear it and leave the assignment untagged. Nothing
        re-tags it automatically.
      </AppText>
      <BtnSecondary
        text="Clear the old topic"
        element-width="auto"
        @click="clearTopic"
      />
    </div>

    <AppText
      v-if="!refCanPick"
      name="body2"
      color="slate-500"
    >
      Choose a grade first — the curriculum is read for the assignment's discipline and grade.
    </AppText>

    <template v-else>
      <div class="assignment-taxonomy-picker__fields">
        <AppSelect
          v-model="refModuleId"
          :options="refModuleOptions"
          label="Module"
          class="assignment-taxonomy-picker__field"
          placeholder="Select a module"
          :disabled="refIsLoading"
          clearable
        />
        <AppSelect
          v-model="refTopicId"
          :options="refTopicOptions"
          label="Topic"
          class="assignment-taxonomy-picker__field"
          placeholder="Select a topic"
          :disabled="refIsLoading || refTopicOptions.length === 0"
          clearable
        />
        <!-- The deepest level, and the one that carries the most meaning for ELA: its topics are
             one-per-module and share the module's name, so the lesson is where a reading
             assignment actually lands in the syllabus. -->
        <AppSelect
          v-model="refLessonId"
          :options="refLessonOptions"
          label="Lesson"
          class="assignment-taxonomy-picker__field"
          placeholder="Select a lesson"
          :disabled="refIsLoading || refLessonOptions.length === 0"
          clearable
        />
      </div>

      <!-- Said out loud rather than left as three empty selects: an empty catalogue is a fact
           about this discipline and grade, not a control that failed to load. -->
      <AppText
        v-if="refIsCatalogueEmpty"
        name="body2"
        color="slate-500"
      >
        The latest curriculum has no modules for this discipline and grade.
      </AppText>
    </template>
  </div>
</template>

<style scoped lang="scss" src="./AssignmentTaxonomyPicker.scss" />
